import { Injectable } from '@angular/core';

import { AngularFireDatabase } from '@angular/fire/database';

import { CollaborationData } from '@models/CollaborationData';
import { first } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { AuthService } from '@services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class CollaborationService {

  STATE_WAITING = "waiting";
  STATE_SUCCEEDED = "succeeded";
  STATE_FAILED = "failed";

  currentCollaborationPath: string = null;

  constructor(
    private authService: AuthService,
    private afdb: AngularFireDatabase
  ) { }

  setStepCollaborationPath(stationId: string, stepId: string) {
    this.currentCollaborationPath = `accounts/${this.authService.currentUser.account_id}/workflow/collaborations/${stationId}/steps/${stepId}`;
  }

  setCurrentCollaborationPath(stationId: string) {
    this.currentCollaborationPath = `accounts/${this.authService.currentUser.account_id}/workflow/collaborations/${stationId}/current`;
  }

  async collaborationStepToCurrent(stationId: string, stepId: string) {
    const collaboration =  await this.getStepCollaboration(stationId, stepId);
    collaboration.collaboration_data.annotating = true;

    await this.saveCollaborationToCurrent(collaboration, stationId);
    return collaboration;
  }

  async collaborationCurrentToStep(stationId: string, stepId: string) {
    const collaboration =  await this.getCurrentCollaboration(stationId);
    if (collaboration) {
      collaboration.collaboration_data.annotating = true;
      collaboration.controls = {
        zoom_ratio: 1.0,
        pdf_scroll_ratio: 0,
        pdf_current_page: 1,
        polar_angle: 0,
        azimuthal_angle: 0,
        final_polar_angle: 0,
        final_azimuthal_angle: 0,
        hide:true,
        close:false,
        page_ratio: 0,
        x: 0.5,
        y: 0.5,
        z: 1,
        refresh: "no",
        revert: "no"
      }
    }

    await this.saveCollaborationToStep(collaboration, stationId, stepId);
    return collaboration;
  }

  getStepCollaboration(stationId: string, stepId: string) {
    return this.afdb.object<any>(`accounts/${this.authService.currentUser.account_id}/workflow/collaborations/${stationId}/steps/${stepId}`).valueChanges().pipe(first()).toPromise();
  }

  getCurrentCollaboration(stationId: string) {
    return this.afdb.object<any>(`accounts/${this.authService.currentUser.account_id}/workflow/collaborations/${stationId}/current`).valueChanges().pipe(first()).toPromise();
  }

  saveCollaborationToStep(collaboration: any, stationId: string, stepId: string) {
    return this.afdb.object(`accounts/${this.authService.currentUser.account_id}/workflow/collaborations/${stationId}/steps/${stepId}`).set(collaboration);
  }

  saveCollaborationToCurrent(collaboration: any, stationId: string) {
    return this.afdb.object(`accounts/${this.authService.currentUser.account_id}/workflow/collaborations/${stationId}/current`).set(collaboration);
  }

  createCollaboration(key: string, name: string, url: string) {
    const uid = this.authService.currentUser.id;
    const uname = this.authService.currentUser.name;
    const collaboration = {
      collaboration_data: {
        type: "image",
        key: key,
        name: name,
        annotating: false,
        status: {
          [uid]: {
            state: "waiting",
            name: uname
          },
          ["dummy"]: {
            state: "succeeded",
            name: ""
          }
        },
        url: url
      },
      controls: {
        zoom_ratio: 1.0,
        pdf_scroll_ratio: 0,
        pdf_current_page: 1,
        polar_angle: 0,
        azimuthal_angle: 0,
        final_polar_angle: 0,
        final_azimuthal_angle: 0,
        hide:true,
        close:false,
        page_ratio: 0,
        x: 0.5,
        y: 0.5,
        z: 1,
        refresh: "no",
        revert: "no"
      }
    };
    return collaboration;
  }

  getCollaborationData(id: string = null): Observable<CollaborationData> {
    const path = this.currentCollaborationPath + '/collaboration_data';
    return this.afdb.object<CollaborationData>(path).valueChanges();
  }

  toggleAnnotate(annotating: boolean){
    this.afdb.object<CollaborationData>(this.currentCollaborationPath + '/collaboration_data').update({annotating: annotating});
  }

  // methods for remote synchronization
  setCollaborationLoaded() { return new Promise<void>(resolve => resolve()) }
  closeCollaboration() { return new Promise<boolean>(resolve => resolve(true)) }
}